// What the agents are doing: the Agent widget (every Claude Code and Codex
// session on the user's linked computers, and the board's own agents, with
// what waits on the user first) and the activity feed. Both at a reading size
// that grows a little with the tile.

import { boardWords } from "../../shared/authors";
import { InstrChange } from "../chrome/InstrChange";
import { useState } from "react";
import { agentName, isBoardChat } from "../../shared/authors";
import { sessionName } from "../../shared/sessionName";
import { Check, ChevronRight, CircleAlert, CircleStop, ExternalLink, Moon, Plug } from "lucide-react";
import { hasStopped } from "../../shared/agentStatus";
import { formatNumber, shortAgo, timeAgo } from "../../shared/format";
import { safeHref } from "../../shared/extract";
import type { Activity, AgentState, RunnerSession } from "../../shared/types";
import { api } from "../lib/api";
import { cx } from "../lib/util";
import { Linked } from "../lib/Linked";
import { useSize } from "../charts/charts";
import { set, useStore } from "../state/store";
import type { WP } from "./data";
import { readingType } from "./plans";
import { useTick } from "./tick";
import { Who } from "../chrome/agentMarks";
import { useSample } from "./WidgetTile";
import { openPlace, placeName, targetOf } from "../state/place";
import { HandsFreeUndo } from "../agent/HandsFreeUndo";
import { DeletedUndo } from "../agent/DeletedUndo";
import { SheetUndo } from "../agent/SheetUndo";
import { PageUndo } from "../agent/PageUndo";
import { readStored, writeStored } from "../lib/stored";
import { useChecklistLeads } from "../onboarding/Checklist";


/** Claude's and Codex's, by an agent's name; another's (Cursor) is neither. */
export const agentOf = (name: string): "claude" | "codex" | null => (/codex/i.test(name) ? "codex" : /claude/i.test(name) ? "claude" : null);
/** Each group's name on its tab: Claude's holds every Claude (Claude Code's sessions, Claude over MCP, the board's chat on
 * Claude), so it says Claude (4 Nov: one Claude beside Codex and the rest). */
export const AGENT_NAME = { claude: "Claude", codex: "Codex" } as const;

/** What a session waits on the user for, as a line. */
export function needLine(x: { wants?: string; tool?: string }): string {
  if (x.wants === "a question") return "Asked you a question";
  if (x.wants === "a plan") return "Has a plan for you to approve";
  return x.tool ? `Wants your OK to use ${x.tool}` : "Wants your OK to go on";
}

/** One line of the Agent widget: a session on the user's computer, or one of the board's own agents, in one shape. */
export interface AgentRow {
  key: string;
  agent: "claude" | "codex" | null;
  /** stopped: it said it was working, then nothing for an hour, or its run ended (Fixes 9) */
  state: "needs_you" | "failed" | "working" | "idle" | "stopped";
  title: string;
  /** what it waits for, what it is doing, or how it ended */
  line: string;
  /** where it runs: the app, the project and, with more than one computer, which */
  where: string;
  at: number;
}

const HOUR = 3_600_000;

/** The sessions and the board's own agents, as rows: what waits on the user first, then what works, then what is done. */
export function agentRows(sessions: RunnerSession[], agents: AgentState[], hosts: number): AgentRow[] {
  const rows: AgentRow[] = [];
  for (const x of sessions)
    rows.push({
      key: `s:${x.host ?? ""}:${x.id}`,
      agent: x.agent,
      state: x.state,
      title: sessionName(x),
      line: x.state === "needs_you" ? needLine(x) : x.state === "working" ? "Working" : "Done",
      where: [x.app, x.title ? x.project : null, hosts > 1 ? x.host : null].filter(Boolean).join(" · "),
      at: x.at,
    });
  const now = Date.now();
  for (const a of agents) {
    const age = now - a.updatedAt;
    // what an agent asked for, or how a job failed, for a day; what it finished, for an hour
    if ((a.status === "needs_you" || a.status === "failed") && age > 24 * HOUR) continue;
    if ((a.status === "done" || a.status === "idle") && age > HOUR) continue;
    // a working that has stopped reads so, with when it was last seen, for a day (Fixes 9)
    const stopped = hasStopped(a, now);
    if (stopped && age > 24 * HOUR) continue;
    const chat = isBoardChat(a.name);
    rows.push({
      key: `a:${a.name}`,
      agent: agentOf(a.name),
      state: stopped ? "stopped" : a.status === "done" || a.status === "idle" ? "idle" : a.status,
      title: a.task || agentName(a.name),
      line: stopped ? `Stopped, last seen ${timeAgo(a.updatedAt, now)}` : a.status === "working" ? a.detail || "Working" : a.status === "needs_you" ? a.detail || "Needs you" : a.status === "failed" ? a.detail || "It stopped" : a.detail || "Done",
      where: chat ? "Your board's chat" : `${a.name}${a.client && a.client !== a.name ? ` · ${a.client}` : ""}`,
      at: a.updatedAt,
    });
  }
  const rank = { needs_you: 0, failed: 0, working: 1, idle: 2, stopped: 3 };
  return rows.sort((a, b) => rank[a.state] - rank[b.state] || b.at - a.at);
}

type Tab = "all" | "claude" | "codex";
const tabKey = (id: string) => `cb:agent-tab:${id}`;

/** Which agents the widget shows, kept on this page for each widget: every one unless the user picks one. */
function useTab(id: string): [Tab, (t: Tab) => void] {
  const [tab, setTab] = useState<Tab>(() => {
    const v = readStored(tabKey(id));
    return v === "claude" || v === "codex" ? v : "all";
  });
  return [
    tab,
    (t) => {
      setTab(t);
      writeStored(tabKey(id), t); // out of reach: this page forgets
    },
  ];
}

function RowLine({ r }: { r: AgentRow }) {
  return (
    <li className={cx("ag-row", `is-${r.state}`)} title={`${r.title}\n${r.line}${r.where ? `\n${r.where}` : ""}`}>
      <span className="ag-mark" aria-hidden>
        {r.state === "working" ? <span className="ag-pulse" /> : r.state === "idle" ? <Check strokeWidth={2.6} /> : r.state === "stopped" ? <CircleStop strokeWidth={2.2} /> : <CircleAlert strokeWidth={2.4} />}
      </span>
      <span className="ag-row-main">
        <b className="ag-row-title">{r.title}</b>
        <span className="ag-row-line">
          {r.state !== "idle" && r.state !== "working" && r.state !== "stopped" ? (
            <em>
              <Linked text={r.line} />
            </em>
          ) : (r.state === "working" && r.line !== "Working") || r.state === "stopped" ? (
            <span className="ag-row-doing">
              <Linked text={r.line} />
            </span>
          ) : null}
          {r.where && (
            <span className="ag-row-where">
              <Linked text={r.where} />
            </span>
          )}
        </span>
      </span>
      <span className="ag-row-at">{r.state === "stopped" ? "" : r.state === "idle" ? shortAgo(r.at) : r.at ? sinceText(r.at) : ""}</span>
    </li>
  );
}

/** How long something has been going: "4m", "1h 20m". */
function sinceText(t: number): string {
  const m = Math.max(0, Math.round((Date.now() - t) / 60_000));
  return m < 1 ? "now" : m < 60 ? `${m}m` : `${Math.floor(m / 60)}h${m % 60 ? ` ${m % 60}m` : ""}`;
}

const SECTIONS: { key: string; label: string; states: AgentRow["state"][] }[] = [
  { key: "need", label: "Waiting for you", states: ["needs_you", "failed"] },
  { key: "work", label: "Working now", states: ["working"] },
  { key: "done", label: "Done in the last hour", states: ["idle"] },
  { key: "stopped", label: "Stopped", states: ["stopped"] },
];

/**
 * What the user's agents are doing: every Claude Code and Codex session on every linked computer (T3 Code's, a
 * terminal's, VS Code's), and the board's own chat, briefs and outside agents. What waits on the user comes first,
 * with what it waits for. On a shared view, only what the board's outside agents said in public.
 */
export function Agent({ item, readOnly }: WP) {
  useTick();
  // the checklist's Connect your agent is on screen: it is the one door, so this offers none of its own
  const leads = useChecklistLeads();
  const storeRunner = useStore((s) => s.runner);
  const sample = useSample();
  const runner = sample?.runner ?? storeRunner;
  const [ref, size] = useSize<HTMLDivElement>();
  const storeAgents = useStore((s) => s.agents);
  const agents = Object.values(sample?.agents ?? storeAgents) as AgentState[];
  const keys = useStore((s) => s.keys);
  const [tab, setTab] = useTab(item.id);
  const style = { fontSize: readingType(size.w, 13, 16) } as React.CSSProperties;
  // a visitor's view, or a board shared with you: the owner's computer and sessions are theirs alone (sharing)
  const guest = useStore((s) => !!s.guest);
  const privateView = (readOnly || guest) && !sample;
  // the user's own sessions: never on a shared view
  const sessions = !privateView && runner?.sessionsOn ? (runner.sessions ?? []) : [];
  const hosts = new Set(sessions.map((x) => x.host)).size;
  const all = agentRows(sessions, privateView ? agents.filter((a) => !a.own) : agents, hosts);
  // the agents there are to choose between: each one ready on a computer, or with a session or a word here
  const have = new Set<"claude" | "codex">();
  for (const c of runner?.computers ?? []) for (const a of c.agents) if (a.ready) have.add(a.id);
  for (const r of all) if (r.agent) have.add(r.agent);
  const tabs: Tab[] = have.size > 1 ? ["all", ...(["claude", "codex"] as const).filter((a) => have.has(a))] : [];
  const shownTab: Tab = tabs.includes(tab) ? tab : "all";
  const rows = shownTab === "all" ? all : all.filter((r) => r.agent === shownTab);
  const count = (t: Tab) => (t === "all" ? all : all.filter((r) => r.agent === t)).filter((r) => r.state !== "idle" && r.state !== "stopped").length;
  const small = size.h > 0 && size.h < 170;
  const linked = (runner?.computers ?? []).length > 0 || !!runner?.online;
  const online = (runner?.computers ?? []).filter((c) => c.online).map((c) => c.host);
  const where = online.length ? online.join(" and ") : (runner?.host ?? "your computer");
  const lastIn = keys.filter((k) => k.kind === "agent" && k.lastUsedAt).sort((a, b) => (b.lastUsedAt ?? 0) - (a.lastUsedAt ?? 0))[0];
  const waiting = rows.filter((r) => r.state === "needs_you" || r.state === "failed").length;
  const working = rows.filter((r) => r.state === "working").length;
  // what to say under the rows, or in place of them: how to get the sessions here, or that there are none
  let foot: React.ReactNode = null;
  if (!privateView && !sample) {
    if (!linked)
      foot = (
        <div className="ag-foot">
          <span>Link your computer to see your Claude Code and Codex sessions here, with what waits for you.</span>
          <button className="btn btn-xs" onClick={() => set({ panel: "connect", panelArg: { computers: true } })} data-interactive>
            <Plug size={12} /> Link your computer
          </button>
        </div>
      );
    else if (runner && !runner.sessionsOn)
      foot = (
        <div className="ag-foot">
          <span>Your own sessions are hidden here.</span>
          <button className="btn btn-xs" onClick={() => void api("/api/runner/sessions", { body: { on: true } }).catch(() => {})} data-interactive>
            Show them
          </button>
        </div>
      );
    else if (!runner?.online) foot = <div className="ag-foot is-quiet">Your computer is off, so its sessions are not shown.</div>;
  }
  // no computer linked and no agent connected yet: the one thing to do, not "All quiet". An agent that connected from
  // outside keeps its line ("Claude Code is connected"), with linking the computer under it
  if (!rows.length && !linked && !lastIn && !privateView && !sample)
    return (
      <div className="ag ag-list is-empty" ref={ref} style={style}>
        <div className="ag-empty">
          <span className="ag-empty-ico">
            <Plug size={18} />
          </span>
          <b>See what your agents are doing</b>
          <span>{leads ? "Connect your agent, and each Claude Code or Codex session shows here as it works." : "Link the computer or server where you use Claude Code or Codex. Each session shows here as it works, with anything waiting for you first."}</span>
          {!leads && (
            <div className="empty-actions">
              <button className="btn btn-sm btn-primary" onClick={() => set({ panel: "connect", panelArg: { computers: true } })} data-interactive>
                Link your computer
              </button>
            </div>
          )}
        </div>
      </div>
    );
  if (!rows.length)
    return (
      <div className="ag ag-list is-empty" ref={ref} style={style}>
        {tabs.length > 0 && <Tabs tabs={tabs} tab={shownTab} setTab={setTab} count={count} />}
        <div className="ag-empty">
          <span className="ag-empty-ico">
            <Moon size={18} />
          </span>
          <b>{privateView ? "No updates yet" : shownTab !== "all" ? `${AGENT_NAME[shownTab]} is quiet` : "All quiet"}</b>
          <span>
            {privateView
              ? "What the agents say shows here."
              : runner?.online && runner.sessionsOn
                ? `Your Claude Code and Codex sessions on ${where} show here as they work, and anything waiting for you comes first.`
                : lastIn
                  ? `${lastIn.label} is connected. What it does shows here.`
                  : "What your agents do shows here."}
          </span>
        </div>
        {foot}
      </div>
    );
  return (
    <div className="ag ag-list" ref={ref} style={style}>
      <div className="ag-top">
        {tabs.length > 0 ? <Tabs tabs={tabs} tab={shownTab} setTab={setTab} count={count} /> : <span className="ag-top-name">{shownTab === "all" ? "Your agents" : AGENT_NAME[shownTab]}</span>}
        <span className="ag-sum">
          {waiting > 0 && <b className="ag-sum-wait">{waiting} waiting</b>}
          {working > 0 && <span>{working} working</span>}
        </span>
      </div>
      <div className="ag-scroll" data-scroll>
        {SECTIONS.map((sec) => {
          const list = rows.filter((r) => sec.states.includes(r.state));
          if (!list.length || (small && sec.key === "done" && rows.length > list.length)) return null;
          return (
            <section key={sec.key} className={cx("ag-sec", `is-${sec.key}`)}>
              <span className="ag-sec-head">{sec.label}</span>
              <ul>
                {list.slice(0, sec.key === "done" ? 4 : 10).map((r) => (
                  <RowLine key={r.key} r={r} />
                ))}
              </ul>
            </section>
          );
        })}
      </div>
      {foot}
    </div>
  );
}

function Tabs({ tabs, tab, setTab, count }: { tabs: Tab[]; tab: Tab; setTab: (t: Tab) => void; count: (t: Tab) => number }) {
  return (
    <div className="ag-tabs" role="tablist" data-interactive>
      {tabs.map((t) => {
        const n = count(t);
        return (
          <button key={t} role="tab" aria-selected={tab === t} className={cx("ag-tab", tab === t && "is-on")} onClick={() => setTab(t)}>
            {t === "all" ? "All" : AGENT_NAME[t]}
            {n > 0 && <span className="ag-tab-n">{n}</span>}
          </button>
        );
      })}
    </div>
  );
}

/** Everything that changed: the board's own log of agents' updates and edits, or a feed's items. */
export function Feed({ item, stream, readOnly }: WP) {
  useTick();
  const [ref, size] = useSize<HTMLDivElement>();
  const sample = useSample();
  const storeActivity = useStore((s) => s.activity);
  const activity = sample?.activity ?? storeActivity;
  const max = Number(item.props.max ?? 30) || 30;
  const style = { fontSize: readingType(size.w, 13, 16.5) };
  if (item.props.source === "stream") {
    const items = (stream?.items ?? []).slice(0, max);
    if (!items.length)
      return (
        <div className="empty">
          <span className="empty-title">Nothing yet</span>
        </div>
      );
    return (
      <div className="fd fd-reader" ref={ref} style={style} data-scroll>
        {items.map((it, i) => (
          <a key={i} className="fd-row" href={it.href ?? undefined} target="_blank" rel="noreferrer noopener" data-interactive>
            <span className="fd-title">{it.label}</span>
            <span className="fd-meta">
              {typeof it.value === "number" && <b>{formatNumber(it.value, null)}</b>}
              {it.sub && <span>{it.sub}</span>}
              {it.t ? <span>{shortAgo(it.t)}</span> : null}
            </span>
          </a>
        ))}
      </div>
    );
  }
  const who = typeof item.props.who === "string" && item.props.who ? item.props.who.toLowerCase() : null;
  const list = activity.filter((a) => !who || a.who.toLowerCase() === who).slice(0, max);
  if (!list.length)
    return (
      <div className="empty">
        <span className="empty-title">Quiet so far</span>
        <span className="empty-sub">What happens on this board lands here: updates your agents post, data that arrives and changes made.</span>
      </div>
    );
  return (
    <div className="fd" ref={ref} style={style} data-scroll>
      {list.map((a: Activity) => {
        const href = safeHref(a.href);
        // a line goes where it happened (Features 1, 20 Oct): its board and the thing on it. A web link keeps
        // opening the page; a line with nowhere to go, or one a visitor or the gallery shows, stays plain
        const to = !href && !readOnly && !sample ? targetOf(a) : null;
        const where = to ? placeName(to) : null;
        const goes = !!to && !!where;
        const open = () => goes && openPlace(to!);
        return (
          <div
            key={a.id}
            className={cx("fd-item", `lvl-${a.level}`, goes && "is-go")}
            {...(goes
              ? {
                  role: "button",
                  tabIndex: 0,
                  "aria-label": `${a.title}. Open ${where}`,
                  onClick: open,
                  onKeyDown: (e: React.KeyboardEvent) => {
                    if (e.key !== "Enter" && e.key !== " ") return;
                    e.preventDefault();
                    open();
                  },
                  "data-interactive": true,
                }
              : {})}
          >
            <Who name={a.who} via={a.via} />
            <div className="fd-main">
              <div className="fd-line">
                <span className="fd-who">{boardWords(a.who)}</span>
                {goes && (
                  <span className="fd-open" aria-hidden>
                    <span>Open {where}</span>
                    <ChevronRight />
                  </span>
                )}
                <span className="fd-time">{shortAgo(a.t)}</span>
              </div>
              <div className="fd-text">
                {href ? (
                  <a href={href} target="_blank" rel="noreferrer noopener" data-interactive>
                    {a.title} <ExternalLink />
                  </a>
                ) : (
                  <Linked text={a.title} />
                )}
              </div>
              {a.body && (
                <div className="fd-body">
                  <Linked text={a.body} />
                </div>
              )}
              {/* an agent's change to a board's instructions: the lines, and Undo (Features 4) */}
              {a.instr && <InstrChange change={a.instr} readOnly={readOnly || !!sample} />}
              {/* a hands-free run's line puts back what it changed, as its chat's Undo does (Features 4) */}
              {a.handsFree && !readOnly && !sample && <HandsFreeUndo to={a.handsFree} />}
              {/* a board an agent deleted comes back whole from the trash (Features 5) */}
              {a.deleted && !readOnly && !sample && <DeletedUndo board={a.deleted.board} />}
              {/* an agent's change to a sheet that cleared or wrote over cells is taken back from the trash (Fixes 9) */}
              {a.sheetUndo && !readOnly && !sample && <SheetUndo trash={a.sheetUndo.trash} />}
              {/* an agent's write on a doc page: the page back to before it, while it is the newest change (Features 7) */}
              {a.pageUndo && !readOnly && !sample && <PageUndo to={a.pageUndo} />}
            </div>
          </div>
        );
      })}
    </div>
  );
}
